import { StepProps } from '@/types/consultant-registration.types';
import { FormField } from '@/components/broker-registration/FormField';
import { TextInput } from '@/components/broker-registration/TextInput';
import { SelectInput } from '@/components/broker-registration/SelectInput';
import { CheckboxGroup } from '@/components/broker-registration/CheckboxGroup';
import {
  COMPENSATION_OPTIONS,
  CURRENCIES,
  AVAILABILITY_OPTIONS,
} from '@/constants/consultant-registration.constants';
import { useLanguage } from '@/contexts/LanguageContext';

export function StepThree({ formData, updateFormData, errors, clearError }: StepProps) {
  const { t } = useLanguage();

  const compensationOptions = COMPENSATION_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(`consultant_registration.options.compensation.${opt.value}`),
  }));

  const availabilityOptions = AVAILABILITY_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(`consultant_registration.options.availability.${opt.value}`),
  }));

  const currencyOptions = CURRENCIES.map((currency) => ({
    value: currency,
    label: currency,
  }));

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold border-b border-gray-700 pb-2 text-white">
        {t('consultant_registration.step_three.title')}
      </h2>

      {/* Error Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-500/20 border-2 border-red-500 rounded-lg p-4">
          <p className="text-red-300 font-semibold">
            {t('consultant_registration.step_three.errors_summary')}
          </p>
          <ul className="list-disc list-inside text-red-200 text-sm mt-2">
            {Object.values(errors).map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Services Description */}
      <FormField
        label={t('consultant_registration.step_three.services')}
        error={errors.services}
        required
      >
        <textarea
          value={formData.services}
          onChange={(e) => {
            updateFormData({ services: e.target.value });
            clearError('services');
          }}
          rows={4}
          placeholder={t('consultant_registration.step_three.services_placeholder')}
          className={`w-full rounded-md border ${
            errors.services ? 'border-red-500' : 'border-gray-600'
          } bg-gray-700 px-4 py-2 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
        />
      </FormField>

      {/* Compensation Options */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">
          {t('consultant_registration.step_three.compensation_title')}
        </h3>

        <div
          className={`grid grid-cols-1 gap-3 p-3 rounded border ${
            errors.compensationOptions ? 'border-red-500' : 'border-gray-600'
          } bg-gray-700/50`}
          data-error={!!errors.compensationOptions}
        >
          {compensationOptions.map((option) => (
            <label key={option.value} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.compensationOptions.includes(option.value)}
                onChange={(e) => {
                  const newOptions = e.target.checked
                    ? [...formData.compensationOptions, option.value]
                    : formData.compensationOptions.filter((o) => o !== option.value);
                  updateFormData({ compensationOptions: newOptions });
                  clearError('compensationOptions');
                }}
                className="h-4 w-4 text-[#CEAB65] rounded"
              />
              <span className="text-sm font-medium text-white">{option.label}</span>
            </label>
          ))}
        </div>
        {errors.compensationOptions && (
          <p className="text-sm text-red-400">{errors.compensationOptions}</p>
        )}

        {/* Compensation Details */}
        <div className="pl-4 border-l-4 border-[#CEAB65] space-y-4">
          {/* Hourly Rate */}
          {formData.compensationOptions.includes('per_hour') && (
            <>
              <FormField
                label={t('consultant_registration.step_three.hourly_rate')}
                error={errors.hourlyRate}
                required
              >
                <TextInput
                  value={formData.hourlyRate}
                  onChange={(value) => {
                    updateFormData({ hourlyRate: value });
                    clearError('hourlyRate');
                  }}
                  placeholder={t('consultant_registration.step_three.hourly_rate_placeholder')}
                  error={!!errors.hourlyRate}
                />
              </FormField>

              <FormField
                label={t('consultant_registration.step_three.hourly_rate_currency')}
                error={errors.hourlyRateCurrency}
                required
              >
                <div className="flex gap-4 p-3 rounded border border-gray-600 bg-gray-700/50">
                  {CURRENCIES.map((currency) => (
                    <label key={currency} className="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="hourlyRateCurrency"
                        value={currency}
                        checked={formData.hourlyRateCurrency === currency}
                        onChange={(e) => {
                          updateFormData({ hourlyRateCurrency: e.target.value as any });
                          clearError('hourlyRateCurrency');
                        }}
                        className="focus:ring-[#CEAB65] h-4 w-4 text-[#CEAB65] border-gray-300"
                      />
                      <span className="ml-2 text-sm font-medium text-white">{currency}</span>
                    </label>
                  ))}
                </div>
              </FormField>
            </>
          )}

          {/* Per Project */}
          {formData.compensationOptions.includes('per_project') && (
            <FormField label={t('consultant_registration.step_three.project_rate')}>
              <textarea
                value={formData.projectRateDetails}
                onChange={(e) => updateFormData({ projectRateDetails: e.target.value })}
                rows={2}
                placeholder={t('consultant_registration.step_three.project_rate_placeholder')}
                className="w-full rounded-md border border-gray-600 bg-gray-700 px-4 py-2 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>
          )}

          {/* Equity Partnership */}
          {formData.compensationOptions.includes('equity_partnership') && (
            <FormField
              label={t('consultant_registration.step_three.equity_partnership')}
              error={errors.equityPartnership}
              required
            >
              <div className="flex gap-4 mt-2">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="equityPartnership"
                    value="yes"
                    checked={formData.equityPartnershipDetails === 'yes'}
                    onChange={(e) => {
                      updateFormData({ equityPartnershipDetails: e.target.value as 'yes' });
                      clearError('equityPartnership');
                    }}
                    className="focus:ring-[#CEAB65] h-4 w-4 text-[#CEAB65] border-gray-300"
                  />
                  <span className="ml-2 text-sm text-white">
                    {t('consultant_registration.options.yes')}
                  </span>
                </label>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="equityPartnership"
                    value="no"
                    checked={formData.equityPartnershipDetails === 'no'}
                    onChange={(e) => {
                      updateFormData({ equityPartnershipDetails: e.target.value as 'no' });
                      clearError('equityPartnership');
                    }}
                    className="focus:ring-[#CEAB65] h-4 w-4 text-[#CEAB65] border-gray-300"
                  />
                  <span className="ml-2 text-sm text-white">
                    {t('consultant_registration.options.no')}
                  </span>
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {t('consultant_registration.step_three.equity_partnership_hint')}
              </p>
            </FormField>
          )}
        </div>
      </div>

      {/* Accepted Currencies */}
      <FormField
        label={t('consultant_registration.step_three.currencies')}
        error={errors.currencies}
        required
      >
        <p className="text-xs text-gray-400 mb-2">
          {t('consultant_registration.step_three.currencies_hint')}
        </p>
        <div
          className={`flex gap-4 p-3 rounded border ${
            errors.currencies ? 'border-red-500' : 'border-gray-600'
          } bg-gray-700/50`}
        >
          {CURRENCIES.map((currency) => (
            <label key={currency} className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.currencies.includes(currency)}
                onChange={(e) => {
                  const newCurrencies = e.target.checked
                    ? [...formData.currencies, currency]
                    : formData.currencies.filter((c) => c !== currency);
                  updateFormData({ currencies: newCurrencies });
                  clearError('currencies');
                }}
                className="h-4 w-4 text-[#CEAB65] rounded"
              />
              <span className="ml-2 text-sm font-medium text-white">{currency}</span>
            </label>
          ))}
        </div>
      </FormField>

      {/* Availability */}
      <FormField
        label={t('consultant_registration.step_three.availability')}
        error={errors.availability}
        required
      >
        <SelectInput
          value={formData.availability}
          onChange={(value) => {
            updateFormData({ availability: value });
            clearError('availability');
          }}
          options={[
            {
              value: '',
              label: t('consultant_registration.step_three.availability_placeholder'),
            },
            ...availabilityOptions,
          ]}
          error={!!errors.availability}
        />
      </FormField>
    </div>
  );
}
